import React, { useState, useEffect } from "react";
import {
  Mail,
  Clock,
  User,
  ArrowRight,
  Search,
  Filter,
  ChevronRight,
  ChevronDown,
  MessageSquare,
} from "lucide-react";

export default function MessagesInbox({ setActiveSection, refreshTrigger }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState(null);

  // Get current user ID from localStorage
  const getCurrentUserId = () => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || user.user_id || user.userId;
      } catch (e) {
        console.warn("Failed to parse user from localStorage");
      }
    }

    const userId = localStorage.getItem("userId") || localStorage.getItem("currentUserId");
    return userId ? parseInt(userId, 10) : null;
  };

  useEffect(() => {
    const fetchThreads = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const currentUserId = getCurrentUserId();
        if (!currentUserId) {
          throw new Error("User ID not found. Please log in again.");
        }

        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/messages/threads/${currentUserId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );
        
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to fetch threads: ${res.status}`);
        }
        
        const data = await res.json();
        setThreads(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching threads:", error);
        setError(error.message);
        setThreads([]);
      } finally {
        setLoading(false);
      }
    };

    fetchThreads();
  }, [refreshTrigger]);

  const markAsRead = async (threadId) => {
    try {
      const currentUserId = getCurrentUserId();
      if (!currentUserId) {
        throw new Error("User ID not found");
      }

      // Optimistically update the UI
      setThreads((prevThreads) =>
        prevThreads.map((thread) =>
          thread.id === threadId ? { ...thread, is_read: true } : thread
        )
      );

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/messages/mark-read/${threadId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({ userId: currentUserId }),
        }
      );

      if (!response.ok) {
        // Revert if API call fails
        setThreads((prevThreads) =>
          prevThreads.map((thread) =>
            thread.id === threadId ? { ...thread, is_read: false } : thread
          )
        );
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to mark as read");
      }
    } catch (error) {
      console.error("Error marking thread as read:", error);
    }
  };

  const handleThreadClick = (thread) => {
    if (!thread || !thread.id) {
      console.error("Invalid thread object or missing thread ID");
      return;
    }

    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      console.error("Current user ID is undefined");
      alert("Unable to get current user ID. Please try logging in again.");
      return;
    }

    // Mark as read if needed
    if (!thread.is_read) {
      markAsRead(thread.id);
    }

    const navigationData = {
      threadId: parseInt(thread.id),
      currentUserId: parseInt(currentUserId),
      recipientId: thread.recipient_id ? parseInt(thread.recipient_id) : null,
      recipientName: thread.recipient_firstName && thread.recipient_lastName 
        ? `${thread.recipient_firstName} ${thread.recipient_lastName}`.trim()
        : 'Unknown User'
    };

    // Validate required data before navigation
    if (!navigationData.threadId || !navigationData.currentUserId) {
      console.error("Missing required navigation data:", navigationData);
      alert("Unable to open conversation. Missing required information.");
      return;
    }

    // Navigate to message thread
    try {
      setActiveSection("message-thread", navigationData);
    } catch (error) {
      console.error("Error navigating to message thread:", error);
      alert("Failed to open conversation. Please try again.");
    }
  };

  // Filter threads based on search term
  const filteredThreads = threads.filter((thread) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    
    const recipientName = thread.recipient_firstName && thread.recipient_lastName 
      ? `${thread.recipient_firstName} ${thread.recipient_lastName}`.toLowerCase()
      : '';
    
    return (
      recipientName.includes(searchLower) ||
      thread.last_message?.toLowerCase().includes(searchLower)
    );
  });

  // Sort threads
  const sortedThreads = [...filteredThreads].sort((a, b) => {
    const dateA = new Date(a.updated_at || 0);
    const dateB = new Date(b.updated_at || 0);
    return sortOption === "newest" ? dateB - dateA : dateA - dateB;
  });

  const getRoleName = (roleId) => {
    const role = roleId?.toString();
    return role === "1" ? "Admin" : role === "2" ? "Sub-Admin" : "Student";
  };

  const getRoleColor = (roleId) => {
    const role = roleId?.toString();
    return role === "1" 
      ? "bg-purple-100 text-purple-700" 
      : role === "2" 
      ? "bg-blue-100 text-blue-700"
      : "bg-green-100 text-green-700";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' });
      } else if (date.toDateString() === yesterday.toDateString()) {
        return "Yesterday";
      } else {
        const daysDiff = Math.floor((today - date) / (1000 * 60 * 60 * 24));
        if (daysDiff < 7) {
          return `${daysDiff}d ago`;
        } else {
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
          });
        }
      }
    } catch (e) {
      return "N/A";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden">
          
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Messages</h1>
                  <p className="text-gray-600 text-sm mt-0.5">
                    {threads.length} {threads.length === 1 ? "conversation" : "conversations"}
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setActiveSection("new-message")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors duration-200"
              >
                <Mail className="w-4 h-4" />
                <span>New Message</span>
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:ring-2 focus:ring-blue-200 focus:border-blue-300 outline-none transition-all duration-200"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Search size={16} />
                </div>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 px-4 py-2 rounded-lg font-medium text-gray-700 transition-colors duration-200"
              >
                <Filter size={14} />
                <span>Filters</span>
                {showFilters ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            </div>

            {showFilters && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex flex-wrap gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Sort by
                    </label>
                    <select
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                      className="bg-white border border-gray-300 rounded-md py-1 px-3 focus:ring-2 focus:ring-blue-200 focus:border-blue-300 outline-none transition-all duration-200"
                    >
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 text-red-700 border-l-4 border-red-500 p-4 mx-6 mt-4 rounded">
              <div className="flex justify-between items-center">
                <span className="text-sm">{error}</span>
                <button 
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700 ml-2"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Conversations List */}
          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mb-3"></div>
                <p className="text-gray-600">Loading conversations...</p>
              </div>
            ) : sortedThreads.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                  <MessageSquare size={24} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? "No matching conversations" : "No conversations yet"}
                </h3>
                <p className="text-gray-500 max-w-sm mx-auto mb-6 text-sm">
                  {searchTerm
                    ? "No conversations match your search criteria."
                    : "Start a conversation by sending your first message."}
                </p>
                <button
                  onClick={() => setActiveSection("new-message")}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 mx-auto transition-colors duration-200"
                >
                  <Mail size={16} />
                  <span>Start Conversation</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedThreads.map((thread, index) => {
                  const recipientName = thread.recipient_firstName && thread.recipient_lastName 
                    ? `${thread.recipient_firstName} ${thread.recipient_lastName}`.trim()
                    : 'Unknown User';

                  return (
                    <div
                      key={thread.id}
                      onClick={() => handleThreadClick(thread)}
                      className="group p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md cursor-pointer transition-all duration-200"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="relative">
                            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-medium text-sm">
                              {recipientName.charAt(0).toUpperCase()}
                            </div>
                            {!thread.is_read && (
                              <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full border border-white"></div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-semibold text-gray-900 text-sm truncate ${!thread.is_read ? 'font-bold' : ''}`}>
                                {recipientName}
                              </span>
                              {thread.recipient_roleId && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(thread.recipient_roleId)}`}>
                                  {getRoleName(thread.recipient_roleId)}
                                </span>
                              )}
                            </div>
                            <p className={`text-sm truncate ${!thread.is_read ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>
                              {thread.last_message || "No messages yet"}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 text-gray-500 text-xs shrink-0">
                          <Clock className="w-3 h-3" />
                          <span className="font-medium whitespace-nowrap">
                            {formatDate(thread.updated_at)}
                          </span>
                          <ArrowRight className="w-3 h-3 text-blue-500 group-hover:translate-x-0.5 transition-transform duration-200" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}